import { FC, useState } from 'react';
import classNames from 'classnames';
import { has_permission } from 'config/permissions';
import Loader from 'components/Loader';
import { CustomTable } from 'components/CustomTable';
import { PagesFilters } from 'components/Filters/PagesFilters';
import { RecentActivity, RecentActivityClickable } from 'components/RecentActivity';
import { numberWithDot } from 'helpers/utils';
import { urlDate } from 'helpers/date';
import { useAppSelector } from 'helpers/hooks';
import { getActiveRole } from 'selectors/admins';
import { getUserClientId } from 'selectors/user';
import { getUserDates } from 'selectors/viewPreferences';
import { useDonorsData } from 'hooks/useDonorsData';
import { getSelectedClient } from 'services/storage';
import { PageModal } from 'components/Modal/pageModal';
import { DashboardScreen, PageCategory, trackEvent } from 'helpers/mixpanelEvents';

const SCREEN: DashboardScreen = 'pages';

interface Props {
  data: any[];
  category: string;
  isLoading: boolean;
  popupType: string;
  setPopupType: (type: string) => void;
  pageColumns: any[];
  subscriptionLevel: string;
  handleDeletePage?: () => void;
  setFilterSelected?: (filter: string) => void;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  closePopup?: () => void;
}

function getRouteFromCategory(category: string): string {
  if (category === 'community') return 'COMMUNITY_PAGES_ROUTE';
  if (category === 'recommendation') return 'RECOMMENDATION_PAGES_ROUTE';
  return 'PAGES_ROUTE';
}

export const PagesTable: FC<Props> = ({
  data,
  category,
  isLoading,
  popupType,
  pageColumns,
  setPopupType,
  subscriptionLevel,
  handleDeletePage,
  setFilterSelected,
  isOpen: externalIsOpen,
  setIsOpen: externalSetIsOpen,
  closePopup: externalClosePopup,
}) => {
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : localIsOpen;
  const setIsOpen = externalSetIsOpen ?? setLocalIsOpen;
  const activeRole = useAppSelector(getActiveRole);
  const userDates = useAppSelector(getUserDates);
  const userClientId = useAppSelector(getUserClientId);
  const [recentActivityMini, setRecentActivityMini] = useState(false);

  const startDate = userDates?.start ? urlDate(userDates.start, 'start') : undefined;
  const endDate = userDates?.end ? urlDate(userDates.end, 'end') : undefined;

  const { donors, isLoading: donorsLoading } = useDonorsData(
    { start_date: startDate ?? '', end_date: endDate ?? '', client_id: userClientId },
    !activeRole || userClientId == null || !startDate || !endDate
  );

  const closePopup = externalClosePopup ?? (() => setIsOpen(false));
  const openCreatePageModal = () => {
    trackEvent('Create Page Opened', {
      screen: SCREEN,
      page_category: category as PageCategory,
    });
    setIsOpen(true);
    setPopupType('create');
  };

  return (
    <>
      <PageModal
        handleDelete={handleDeletePage}
        popupType={popupType}
        isOpen={isOpen}
        closePopup={closePopup}
        subscriptionLevel={subscriptionLevel}
        screen={SCREEN}
        pageCategory={category}
      />
      {isLoading && <Loader type="dots" />}
      {!has_permission(getRouteFromCategory(category), 'R', activeRole, subscriptionLevel) ? (
        <div className="content">
          <h1 className="title-permission">
            Sorry, you don&apos;t have permission to see this screen.
          </h1>
        </div>
      ) : (
        <>
          <div className="row filters-row">
            <PagesFilters
              setFilterSelected={setFilterSelected}
              allValue="Pages"
              onlyAllValue={!setFilterSelected}
              screen={SCREEN}
              pageCategory={category}
            />
            <RecentActivityClickable
              isMini={recentActivityMini}
              setIsMini={setRecentActivityMini}
              screen={SCREEN}
            />
          </div>
          <div className={classNames('content', { 'content-mini': recentActivityMini })}>
            <RecentActivity isMini={recentActivityMini} donors={donors} isLoading={donorsLoading} />
            <div className="row custom-header-container">
              <div className="grid-column col-lg-9 col-md-8 col-sm-12">
                <span className="oath-h3 inkBlue capitalize">
                  {getSelectedClient()?.name} {category} Pages
                </span>
                <span className="oath-body inkBlue">{numberWithDot(data?.length)} Total</span>
              </div>
              {has_permission(
                getRouteFromCategory(category),
                'C',
                activeRole,
                subscriptionLevel
              ) && (
                <div className="col-lg-3 col-md-4 col-sm-12 new-page-button">
                  <button
                    type="button"
                    className="btn-inkBlue btn btn-secondary btn-block"
                    onClick={openCreatePageModal}
                  >
                    Create New Page
                  </button>
                </div>
              )}
            </div>
            <div className="row">
              <div className="col-lg-12">
                <CustomTable
                  key={category}
                  subtitle="A full list of your pages can be found below. Reach out to partners@oath.vote if you need support."
                  data={data}
                  columns={pageColumns}
                  withPagination
                  screen={SCREEN}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
